import { shouldShowRentalCertificateModal } from '@/components/PartnerIntegrations/RentalCertificateModal/functions';
import { InteractionGoal } from '@/helpers/GTM/GTMParameters';
import { getRandomNumber } from '@/helpers/LegalProtection/LegalProtectionHelper';
import { TenantPlusModalOpenTrigger } from '@/helpers/TenantPlus/TenantPlusModalOpenTriggers';
import { generateTenantPlusTrackingEntryPoint } from '@/helpers/TenantPlus/TenantPlusUrls';
import { HgLocalStorageKey } from '@/models';
import { Listing, OfferType } from '@smg-real-estate/hg-rets-schema';
import { StorageHelper } from '@smg-real-estate/hg-search-libs';
import { Ref } from 'vue';

/**
 * Sticky modal selector composable
 * Encapsulates weighted-random modal selection logic
 */
export const useStickyModalSelector = ({
  listing,
  storageHelper,
  isRentalCertificateModalVisible,
  isCrifModalVisible,
  isFinancingModalVisible,
  isLegalProtectionModalVisible,
  tenantPlusTrackingEntryPoint,
}: {
  listing: Ref<Listing | undefined>;
  storageHelper: StorageHelper;
  isRentalCertificateModalVisible: Ref<boolean>;
  isCrifModalVisible: Ref<boolean>;
  isFinancingModalVisible: Ref<boolean>;
  isLegalProtectionModalVisible: Ref<boolean>;
  tenantPlusTrackingEntryPoint: Ref<string>;
}) => {
  const selectStickyModalToShow = () => {
    const allModals = [
      // Rent listing modals (total weight = 100)
      {
        shouldShow: shouldShowCrifModal(),
        weight: 50,
        bottomSheetType: InteractionGoal.ORDER_DEBT_COLLECTION_REGISTER,
        open: openCrifModalForRentObject,
      },
      {
        shouldShow: listing.value
          ? shouldShowRentalCertificateModal(listing.value, storageHelper)
          : false,
        weight: 50,
        bottomSheetType: InteractionGoal.RENTAL_PROVISIONAL_CERT_SWISSCAUTION,
        open: openRentalCertificateModal,
      },

      // Buy listing modals (total weight = 100)
      {
        shouldShow: shouldShowFinancingModal(),
        weight: 60,
        bottomSheetType: InteractionGoal.FINANCIAL_CONSULTATION,
        open: openFinancingModalForBuyObject,
      },
      {
        shouldShow: shouldShowLegalProtectionModal(),
        weight: 40,
        bottomSheetType: InteractionGoal.LEGAL_PROTECTION_INSURANCE,
        open: openLegalProtectionModal,
      },
    ];

    const qualifiedModals = allModals.filter((modal) => modal.shouldShow);

    if (qualifiedModals.length === 0) {
      return undefined;
    }

    if (qualifiedModals.length === 1) {
      return qualifiedModals[0];
    }

    const totalWeight = qualifiedModals.reduce((sum, modal) => sum + modal.weight, 0);

    const threshold = getRandomNumber() * totalWeight;
    let cumulativeWeight = 0;

    for (const modal of qualifiedModals) {
      cumulativeWeight += modal.weight;
      if (threshold <= cumulativeWeight) {
        return modal;
      }
    }

    // Safety fallback (should never happen)
    return qualifiedModals[qualifiedModals.length - 1];
  };
  const shouldShowCrifModal = (): boolean => {
    return (
      listing.value?.offerType === OfferType.Rent &&
      storageHelper.getLocalStorageExpirableItem(HgLocalStorageKey.CRIF_MODAL_LAST_DISPLAYED) ===
        null
    );
  };
  const shouldShowFinancingModal = (): boolean => {
    return (
      listing.value?.offerType === OfferType.Buy &&
      storageHelper.getLocalStorageExpirableItem(
        HgLocalStorageKey.FINANCING_MODAL_LAST_DISPLAYED,
      ) === null
    );
  };
  const shouldShowLegalProtectionModal = (): boolean => {
    // Shows protekta modal for Buy listings only and if it hasn't been shown in the last 30 minutes
    const wasDisplayedRecently =
      storageHelper.getLocalStorageExpirableItem(
        HgLocalStorageKey.LEGAL_PROTECTION_MODAL_LAST_DISPLAYED,
      ) !== null;

    return listing.value?.offerType === OfferType.Buy && !wasDisplayedRecently;
  };
  const openCrifModalForRentObject = () => {
    isCrifModalVisible.value = true;
    tenantPlusTrackingEntryPoint.value = generateTenantPlusTrackingEntryPoint(
      listing.value?.offerType,
      TenantPlusModalOpenTrigger.CRIF_MODAL,
    );
    saveCrifModalLastTimeDisplayed();
  };
  const saveCrifModalLastTimeDisplayed = () => {
    storageHelper.setLocalStorageExpirableItem(
      HgLocalStorageKey.CRIF_MODAL_LAST_DISPLAYED,
      true, // unused, set for formality
      1800, // 30 minutes
    );
  };
  const openFinancingModalForBuyObject = () => {
    isFinancingModalVisible.value = true;
    storageHelper.setLocalStorageExpirableItem(
      HgLocalStorageKey.FINANCING_MODAL_LAST_DISPLAYED,
      true, // unused, set for formality
      1800, // 30 minutes
    );
  };
  const openLegalProtectionModal = () => {
    isLegalProtectionModalVisible.value = true;
    storageHelper.setLocalStorageExpirableItem(
      HgLocalStorageKey.LEGAL_PROTECTION_MODAL_LAST_DISPLAYED,
      true,
      1800, // 30 minutes
    );
  };
  const openRentalCertificateModal = () => {
    isRentalCertificateModalVisible.value = true;
    storageHelper.setLocalStorageExpirableItem(
      HgLocalStorageKey.RENTAL_CERTIFICATE_MODAL_LAST_DISPLAYED,
      true, // unused, set for formality
      1800, // 30 minutes
    );
  };

  return {
    selectStickyModalToShow,
  };
};
